import React, { useState } from 'react';
import {
  Button,
  Form,
  Upload,
  Tag,
  message as _message,
  Popconfirm
} from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  WarningOutlined
} from '@ant-design/icons';
import moment from 'moment';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import {
  formItemLayout,
  tailFormItemLayout
} from '../../utils/constants/formLayout';
import useHandleResize from '../../utils/HandleResize';

const MissingDocUpload = ({ doc, contractId, setRefresh }) => {
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const [fileList, setFileList] = useState([]);
  const { dispatchAPI } = useAuthContext();
  const [form] = Form.useForm();
  const { width } = useHandleResize();
  const isXLScreen = width > 1800;

  const isLastDocRefused = () => {
    const history = doc.history;
    const lastDoc = history[history?.length - 1];

    return history.length
      ? `Manquant (Document refusé le ${moment(lastDoc.date).format(
          'L'
        )} à ${moment(lastDoc.date).format('HH:mm')} - ${lastDoc.comment})`
      : 'Manquant';
  };

  const TagStatus = (status) => {
    if (!doc.required) {
      return (
        <Tag icon={<InfoCircleOutlined />} color="cyan">
          Non-obligatoire
        </Tag>
      );
    }
    if (status === 'SAVED') {
      return (
        <Tag
          icon={<SaveOutlined />}
          color="success"
          style={{ marginBottom: 5 }}
        >
          Enregistré
        </Tag>
      );
    }
    if (status === 'SENT') {
      return (
        <Tag icon={<CheckCircleOutlined />} color="processing">
          Envoyé avec succès
        </Tag>
      );
    }
    if (status === 'VALID') {
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Validé
        </Tag>
      );
    }
    if (status === 'MISSING') {
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          {isLastDocRefused()}
        </Tag>
      );
    }
    return <div />;
  };

  const downloadFile = async (file) => {
    if (file === null) return;
    try {
      const response = await dispatchAPI('GET', {
        url: `/contracts/files/${contractId}/${file._id}`,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: response.data.type
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.metadata.originalName}`;
      a.click();
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const onFinish = async () => {
    if (!fileList || fileList.length === 0) {
      _message.success(`Aucun fichier ajouté`);
      return;
    }
    try {
      const formData = new FormData();
      if (fileList) {
        for (let i = 0; i < fileList.length; i += 1) {
          formData.append('files', fileList[i]);
        }
      }
      await dispatchAPI('POST', {
        url: `/contractDocuments/${contractId}/${doc.name}?amendment=${doc.amendmentNumber}`,
        body: formData
      });
      _message.success(`Document chargé avec succès`);
      setRefresh();
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const deleteFile = async (file) => {
    try {
      await dispatchAPI('POST', {
        url: `/contractDocuments/${contractId}/delete`,
        body: { file: file._id, type: doc.name }
      });
      _message.success(`${file.metadata.originalName} est supprimé`);
      setRefresh();
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getLabel = (document) => {
    return `${t(`contractDocuments.${document.name}`)}${
      document.amendmentNumber > 0
        ? ` (avenant N°${document.amendmentNumber})`
        : ''
    }`;
  };

  return (
    <div style={{ width: '100%' }}>
      <Form form={form} onFinish={onFinish} {...formItemLayout}>
        <Form.Item
          className="missing-parts-formitem"
          name="files"
          label={getLabel(doc)}
          rules={[{ required: false }]}
          {...tailFormItemLayout}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: isXLScreen ? 'row' : 'column',
              flexWrap: 'wrap',
              alignItems: isXLScreen ? 'center' : 'normal',
              justifyContent: isXLScreen ? 'space-between' : 'inherit'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap'
              }}
            >
              {TagStatus(doc.status)}
              {doc.status === 'SAVED' && (
                <div>
                  {doc.files?.map((file) => {
                    return (
                      <div
                        style={{
                          width: 300,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          margin: '2px 5px'
                        }}
                      >
                        <Button type="link" onClick={() => downloadFile(file)}>
                          {file?.metadata?.originalName}
                        </Button>
                        <Popconfirm
                          title={t(
                            'Êtes-vous sûr de vouloir supprimer ce fichier ?'
                          )}
                          okText={t('Supprimer')}
                          okButtonProps={{ type: 'danger' }}
                          cancelText={t(
                            'datatable.column.action.delete.cancel'
                          )}
                          onConfirm={() => {
                            deleteFile(file);
                          }}
                          icon={<WarningOutlined />}
                        >
                          <DeleteOutlined
                            style={{
                              fontSize: 16,
                              color: 'var(--secondaryColor)'
                            }}
                            type="delete"
                          />
                        </Popconfirm>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexFlow: 'row wrap',
                justifyContent: isXLScreen ? 'flex-end' : 'inherit'
              }}
            >
              {doc.status !== 'VALID' && doc.status !== 'SENT' && (
                <>
                  <Upload
                    maxCount={5}
                    fileList={fileList}
                    beforeUpload={(file) => {
                      setFileList([...fileList, file]);
                      return false;
                    }}
                    onRemove={({ uid }) =>
                      setFileList(fileList.filter((file) => file.uid !== uid))
                    }
                  >
                    <Button
                      icon={<UploadOutlined />}
                      type="primary"
                      style={{ margin: '5px 0px 8px 0px' }}
                    >
                      {t(`buttons.upload`)}
                    </Button>
                  </Upload>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Button
                      type="add"
                      icon={<SaveOutlined />}
                      htmlType="submit"
                      {...tailFormItemLayout}
                      style={{ margin: '5px 8px 5px 8px' }}
                    >
                      {t(`buttons.save`)}
                    </Button>
                    <span style={{ color: 'gray', fontSize: 13 }}>
                      Les documents enregistrés ne sont pas envoyés
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

MissingDocUpload.propTypes = {
  doc: PropTypes.shape({
    name: PropTypes.string,
    status: PropTypes.string,
    files: PropTypes.shape([]),
    required: PropTypes.bool,
    history: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        comment: PropTypes.string
      })
    ),
    amendmentNumber: PropTypes.string
  }).isRequired,
  contractId: PropTypes.string.isRequired,
  setRefresh: PropTypes.func.isRequired
};

export default MissingDocUpload;
